import React, {FC} from 'react';

import modelAiWrite1 from 'assets/v3/guide/model/ai-write-1.webp';
import modelAiWrite2 from 'assets/v3/guide/model/ai-write-2.webp';
import modelEntry from 'assets/v3/guide/model/entry.webp';
import modelRegen from 'assets/v3/guide/model/regen.webp';
import modelShowClothes1 from 'assets/v3/guide/model/show-clothes-1.webp';
import modelShowClothes2 from 'assets/v3/guide/model/show-clothes-2.webp';
import modelShowCup1 from 'assets/v3/guide/model/show-cup-1.webp';
import modelShowCup2 from 'assets/v3/guide/model/show-cup-2.webp';
import modelShowEarring1 from 'assets/v3/guide/model/show-earring-1.webp';
import modelShowEarring2 from 'assets/v3/guide/model/show-earring-2.webp';
import modelShowLamp1 from 'assets/v3/guide/model/show-lamp-1.webp';
import modelShowLamp2 from 'assets/v3/guide/model/show-lamp-2.webp';
import modelShowNecklace1 from 'assets/v3/guide/model/show-necklace-1.webp';
import modelShowNecklace2 from 'assets/v3/guide/model/show-necklace-2.webp';
import modelShowPruner1 from 'assets/v3/guide/model/show-pruner-1.webp';
import modelShowPruner2 from 'assets/v3/guide/model/show-pruner-2.webp';
import modelUploadFront from 'assets/v3/guide/model/upload-front.webp';
import modelUploadScene from 'assets/v3/guide/model/upload-scene.webp';
import sceneAiWrite1 from 'assets/v3/guide/scene/ai-write-1.webp';
import sceneAiWrite2 from 'assets/v3/guide/scene/ai-write-2.webp';
import sceneEntry from 'assets/v3/guide/scene/entry.webp';
import sceneRegen from 'assets/v3/guide/scene/regen.webp';
import sceneShowEarphone1 from 'assets/v3/guide/scene/show-earphone-1.webp';
import sceneShowEarphone2 from 'assets/v3/guide/scene/show-earphone-2.webp';
import sceneShowFeeder1 from 'assets/v3/guide/scene/show-feeder-1.webp';
import sceneShowFeeder2 from 'assets/v3/guide/scene/show-feeder-2.webp';
import sceneShowNecklace1 from 'assets/v3/guide/scene/show-necklace-1.webp';
import sceneShowNecklace2 from 'assets/v3/guide/scene/show-necklace-2.webp';
import sceneShowOil1 from 'assets/v3/guide/scene/show-oil-1.webp';
import sceneShowOil2 from 'assets/v3/guide/scene/show-oil-2.webp';
import sceneShowPerfume1 from 'assets/v3/guide/scene/show-perfume-1.webp';
import sceneShowPerfume2 from 'assets/v3/guide/scene/show-perfume-2.webp';
import sceneShowPruner1 from 'assets/v3/guide/scene/show-pruner-1.webp';
import sceneShowPruner2 from 'assets/v3/guide/scene/show-pruner-2.webp';
import sceneShowUmbrella1 from 'assets/v3/guide/scene/show-umbrella-1.webp';
import sceneShowUmbrella2 from 'assets/v3/guide/scene/show-umbrella-2.webp';
import sceneUploadSample from 'assets/v3/guide/scene/upload-sample.webp';

/** 使用指南「模特/场景图」tab 英文正文（与 zh.tsx 元素结构 1:1） */
const EnContent: FC = () => {
  return (
    <>
      <h2>Product Scene Image Generation</h2>

      <h3>1. Generating Scene Images</h3>
      <img alt="Scene generation entry" decoding="async" loading="lazy" src={sceneEntry} />
      <p>Upload 1–3 product photos. Complete and varied photos produce better, more accurate product scene images.</p>
      <img alt="Sample product upload" decoding="async" loading="lazy" src={sceneUploadSample} />
      <p>
        If you have specific scene requirements, click “+ Enter a text description (optional)” and type them in; if not,
        just leave it to AI.
      </p>
      <p>When writing the description, keep in mind:</p>
      <ul>
        <li>
          Describe a concrete scene instead of something vague like “indoors”. Describe the scene you picture for the
          product, for example: the product on a coffee table in a living room, with flowers in the foreground and a
          water cup beside it.
        </li>
        <li>
          Scene images may randomly generate interactions between the product and people. If no people are wanted, add
          to the description: no people should appear in the image.
        </li>
      </ul>
      <p>
        Choose the model, aspect ratio, resolution and number of images, then click the 【Generate Now】button — that is
        all it takes.
      </p>

      <h3>2. Generate Again</h3>
      <p>
        Need more scene images? On the same page, simply click the 【Generate Again】button. For specific scene
        requirements, click “+ Enter a text description”.
      </p>
      <img alt="Generate again" decoding="async" loading="lazy" src={sceneRegen} />
      <p>Out of ideas? Try AI Write, then tweak the AI prompt and generate again.</p>
      <img alt="AI Write" decoding="async" loading="lazy" src={sceneAiWrite1} />
      <img alt="Edit the AI prompt and regenerate" decoding="async" loading="lazy" src={sceneAiWrite2} />

      <h3>3. Showcase</h3>
      <h4>Branch Pruner</h4>
      <figure className="v3-img-pair">
        <img alt="Branch pruner scene 1" decoding="async" loading="lazy" src={sceneShowPruner1} />
        <img alt="Branch pruner scene 2" decoding="async" loading="lazy" src={sceneShowPruner2} />
      </figure>
      <h4>Perfume</h4>
      <figure className="v3-img-pair">
        <img alt="Perfume scene 1" decoding="async" loading="lazy" src={sceneShowPerfume1} />
        <img alt="Perfume scene 2" decoding="async" loading="lazy" src={sceneShowPerfume2} />
      </figure>
      <h4>Earphones</h4>
      <figure className="v3-img-pair">
        <img alt="Earphones scene 1" decoding="async" loading="lazy" src={sceneShowEarphone1} />
        <img alt="Earphones scene 2" decoding="async" loading="lazy" src={sceneShowEarphone2} />
      </figure>
      <h4>Umbrella</h4>
      <figure className="v3-img-pair">
        <img alt="Umbrella scene 1" decoding="async" loading="lazy" src={sceneShowUmbrella1} />
        <img alt="Umbrella scene 2" decoding="async" loading="lazy" src={sceneShowUmbrella2} />
      </figure>
      <h4>Pet Feeder</h4>
      <figure className="v3-img-pair">
        <img alt="Pet feeder scene 1" decoding="async" loading="lazy" src={sceneShowFeeder1} />
        <img alt="Pet feeder scene 2" decoding="async" loading="lazy" src={sceneShowFeeder2} />
      </figure>
      <h4>Necklace</h4>
      <figure className="v3-img-pair">
        <img alt="Necklace scene 1" decoding="async" loading="lazy" src={sceneShowNecklace1} />
        <img alt="Necklace scene 2" decoding="async" loading="lazy" src={sceneShowNecklace2} />
      </figure>
      <h4>Essential Oil</h4>
      <figure className="v3-img-pair">
        <img alt="Essential oil scene 1" decoding="async" loading="lazy" src={sceneShowOil1} />
        <img alt="Essential oil scene 2" decoding="async" loading="lazy" src={sceneShowOil2} />
      </figure>

      <h2>Product Model Image Generation</h2>

      <h3>1. Generating Model Images</h3>
      <img alt="Model generation entry" decoding="async" loading="lazy" src={modelEntry} />
      <p>
        Upload 1–3 product photos. Complete and varied photos produce better, more accurate model showcase images. Front
        and back views of the product work best, for example:
      </p>
      <img alt="Sample front-view upload" decoding="async" loading="lazy" src={modelUploadFront} />
      <p>Front view</p>
      <img alt="Sample AI scene" decoding="async" loading="lazy" src={modelUploadScene} />
      <p>AI scene image</p>
      <p>
        If you have specific model requirements, click “+ Enter a text description (optional)” and type them in; if not,
        just leave it to AI.
      </p>
      <p>When writing the description, describe the model you want:</p>
      <ul>
        <li>Gender</li>
        <li>Age or age range (young adult / middle-aged / senior, or a specific age)</li>
        <li>Skin tone (e.g. Asian, White, Black, Latino, etc.)</li>
        <li>Others (expression, hairstyle, etc.)</li>
      </ul>
      <p>
        To customize the scene the model is in, again avoid something vague like “indoors” — write, for example: in an
        American vintage bedroom.
      </p>
      <p>Example description: female, 20 years old, white, curly hair, in an American vintage bedroom.</p>
      <p>
        Choose the model, aspect ratio, resolution and number of images, then click the 【Generate Now】button — that is
        all it takes.
      </p>

      <h3>2. Generate Again</h3>
      <p>
        Need more model images? On the generation page, simply click the 【Generate Again】button. For specific scene
        requirements, click “+ Enter a text description (optional)”.
      </p>
      <img alt="Generate again" decoding="async" loading="lazy" src={modelRegen} />
      <p>Out of ideas? Try AI Write, then tweak the AI prompt and generate again.</p>
      <img alt="AI Write" decoding="async" loading="lazy" src={modelAiWrite1} />
      <img alt="Edit the AI prompt and regenerate" decoding="async" loading="lazy" src={modelAiWrite2} />

      <h3>3. Showcase</h3>
      <h4>Clothes</h4>
      <figure className="v3-img-pair">
        <img alt="Clothes model showcase 1" decoding="async" loading="lazy" src={modelShowClothes1} />
        <img alt="Clothes model showcase 2" decoding="async" loading="lazy" src={modelShowClothes2} />
      </figure>
      <h4>Cup</h4>
      <figure className="v3-img-pair">
        <img alt="Cup model showcase 1" decoding="async" loading="lazy" src={modelShowCup1} />
        <img alt="Cup model showcase 2" decoding="async" loading="lazy" src={modelShowCup2} />
      </figure>
      <h4>Necklace</h4>
      <figure className="v3-img-pair">
        <img alt="Necklace model showcase 1" decoding="async" loading="lazy" src={modelShowNecklace1} />
        <img alt="Necklace model showcase 2" decoding="async" loading="lazy" src={modelShowNecklace2} />
      </figure>
      <h4>Desk Lamp</h4>
      <figure className="v3-img-pair">
        <img alt="Desk lamp model showcase 1" decoding="async" loading="lazy" src={modelShowLamp1} />
        <img alt="Desk lamp model showcase 2" decoding="async" loading="lazy" src={modelShowLamp2} />
      </figure>
      <h4>Branch Pruner</h4>
      <figure className="v3-img-pair">
        <img alt="Branch pruner model showcase 1" decoding="async" loading="lazy" src={modelShowPruner1} />
        <img alt="Branch pruner model showcase 2" decoding="async" loading="lazy" src={modelShowPruner2} />
      </figure>
      <h4>Earrings</h4>
      <figure className="v3-img-pair">
        <img alt="Earrings model showcase 1" decoding="async" loading="lazy" src={modelShowEarring1} />
        <img alt="Earrings model showcase 2" decoding="async" loading="lazy" src={modelShowEarring2} />
      </figure>
    </>
  );
};

export default EnContent;
